import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { QuoteType } from "@/lib/interfaces and Enums/ENUMQuoteType";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import { IBlockAssigned, ItradeBlockData } from "@/store/tradeBlockDataStore";
import { ITradeSettingsStore } from "@/store/tradeSettingsStore";
import { resolveSettingForCP } from "../../ActionSummaryOP/selectPanel/SelectPanelHelper";

export const statusAllowEditNPV = [
  ExecutionStrategyStatus.New,
  ExecutionStrategyStatus.Recalled,
  ExecutionStrategyStatus.NotDealt,
];

export const statusAllowEditRecoupon = [
  ...statusAllowEditNPV,
  ExecutionStrategyStatus.Sent,
  ExecutionStrategyStatus.Quoted,
  ExecutionStrategyStatus.OffQuote,
  ExecutionStrategyStatus.ReSent,
  ExecutionStrategyStatus.Awarded,
];

export function isTwoWayQuoteType(
  thisTradeSettings: ITradeSettingsStore | undefined,
  thisTrade: ExecutionStrategy | undefined,
): boolean {
  if (thisTradeSettings && thisTrade?.status === ExecutionStrategyStatus.New) {
    if (
      thisTradeSettings.quoteType.find((toggle) => toggle.active)?.name ===
      QuoteType.TwoWay
    ) {
      return true;
    }
    // LQC-2370: unwind is per block and shared by every CP, so one active CP
    // overridden to Two-Way blocks NPV trade-wide, same as the trade-level Side.
    return (thisTradeSettings.counterpartiesList ?? []).some(
      (cp) =>
        cp.active &&
        resolveSettingForCP(thisTradeSettings, cp.code, "quoteType").find(
          (toggle) => toggle.active,
        )?.name === QuoteType.TwoWay,
    );
  } else if (thisTrade?.quoteType === "Two-Way") {
    return true;
  }
  return false;
}

export function isBlockDisabledForNPV(
  block: IBlockAssigned,
  status: ExecutionStrategyStatus,
  isTwoWay: boolean,
): boolean {
  if (!statusAllowEditNPV.includes(status)) {
    return true;
  }

  // Close = The Trade HAS TO BE NPV. The user cannot access the checkbox.
  if (block.openClose === "Close" && block.unwind === "NPV") {
    return true;
  }

  // TWO way trades cannot be NPV.
  if (isTwoWay) {
    return true;
  }

  return false;
}

export function isBlockDisabledForRecoupon(
  block: IBlockAssigned,
  status: ExecutionStrategyStatus,
): boolean {
  if (!statusAllowEditRecoupon.includes(status)) {
    return true;
  }

  // Close = The Trade HAS TO BE NPV. The user cannot access the recoupon box.
  if (block.openClose === "Close" && block.unwind === "NPV") {
    return true;
  }

  // DON'T allow Recoupon cells to be changed if NPV is already selected and past New status
  if (block.unwind === "NPV" && !statusAllowEditNPV.includes(status)) {
    return true;
  }

  return false;
}

export interface BlockCheckboxState {
  block: IBlockAssigned;
  isChecked: boolean;
  isDisabled: boolean;
}

export function getBlocksCheckboxState(
  blocks: IBlockAssigned[],
  thisTrade: ExecutionStrategy | undefined,
  thisTradeSettings: ITradeSettingsStore | undefined,
  isNPV: boolean,
): BlockCheckboxState[] {
  if (!thisTrade) {
    return [];
  }

  const status = thisTrade.status as ExecutionStrategyStatus;
  const isTwoWay = isTwoWayQuoteType(thisTradeSettings, thisTrade);

  // Only SWP blocks have checkboxes
  const swapBlocks = blocks.filter((block) => block.instrumentType === "SWP");

  return swapBlocks.map((block) => {
    const isDisabled = isNPV
      ? isBlockDisabledForNPV(block, status, isTwoWay)
      : isBlockDisabledForRecoupon(block, status);

    const isChecked = isNPV
      ? block.unwind === "NPV"
      : block.unwind === "Recoupon";

    return {
      block,
      isChecked,
      isDisabled,
    };
  });
}

export function getHeaderCheckboxState(blockStates: BlockCheckboxState[]): {
  isChecked: boolean;
  isIndeterminate: boolean;
  isDisabled: boolean;
  enabledBlocks: BlockCheckboxState[];
} {
  const enabledBlocks = blockStates.filter((state) => !state.isDisabled);

  // If all blocks are disabled, the header checkbox should be disabled
  if (enabledBlocks.length === 0) {
    return {
      isChecked: false,
      isIndeterminate: false,
      isDisabled: true,
      enabledBlocks: [],
    };
  }

  const checkedEnabledBlocks = enabledBlocks.filter((state) => state.isChecked);

  const allEnabledAreChecked =
    checkedEnabledBlocks.length === enabledBlocks.length;
  const someEnabledAreChecked =
    checkedEnabledBlocks.length > 0 && !allEnabledAreChecked;

  return {
    isChecked: allEnabledAreChecked,
    isIndeterminate: someEnabledAreChecked,
    isDisabled: false,
    enabledBlocks,
  };
}

export function getBlocksForTradeFromStore(
  tradeBlockData: ItradeBlockData[],
  esid: string,
): IBlockAssigned[] {
  const tradeBlocks = tradeBlockData.find((blocks) => blocks.esid === esid);
  return tradeBlocks?.blocks ?? [];
}
